import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TableSortLabel, Paper, IconButton, Chip, TextField, MenuItem, Autocomplete,
  Grid, Pagination, Dialog, DialogActions, DialogContent, DialogTitle, Alert } from '@mui/material';
import { Edit, Delete, Visibility, Add, Refresh, AutoFixHigh } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AdminService } from '../../services/adminService';
import {
  ROUTES,
  GAME_TYPES,
  DOMAIN_NAMES,
  getAllAvailableNotions,
  getCompetenceName,
  getNotionLabel,
} from '../../config/shared_config';
import AdminLayout from '../../layouts/AdminLayout';
import LoadingScreen from '../../components/LoadingScreen';
import BulkCorrectionModal from '../../components/admin/BulkCorrectionModal';
import {
  FILTRES_PAR_DEFAUT,
  lireFiltresListe,
  ecrireFiltresListe,
  lirePageListe,
  ecrirePageListe,
  parametresApiListe,
} from '../../utils/exerciseListFilters';

/**
 * Libellés lisibles pour les sources posées par le pipeline de revue. Toutes les autres
 * (les lots `gen-*`) s'affichent telles quelles : la liste vient de la base, on ne la
 * maintient plus à la main — c'est précisément ce qui rendait les lots introuvables.
 */
const SOURCE_LABELS = {
  llm: 'LLM',
  admin: 'Admin',
  'LLM-bulk': 'LLM-bulk',
  'import-batch': 'Import',
  'ai-autovalide': '🤖 Auto-validé (IA)',
  'ai-corrige': '🤖 Corrigé (IA)',
  'ai-review': '🤖 À revoir (IA)',
  'ai-kill': '🤖 Désactivé (IA)',
};

/** Libellés des valeurs de `type` (l'origine d'un exercice) ; les autres s'affichent telles quelles. */
const TYPE_LABELS = {
  determ: 'Déterministe',
  deterministe: 'Déterministe',
  llm: 'LLM',
  manual: 'Manuel',
};

/** Colonnes triables : clé de tri de l'API → en-tête. */
const COLONNES_TRIABLES = {
  id: 'ID',
  game_type: 'Type de jeu',
  created_at: 'Créé le',
  updated_at: 'Modifié le',
  validation_status: 'Validation',
  parties: 'Joué',
  taux_echec: 'Échec',
};

/** Seuil de parties sous lequel le serveur ne donne pas de taux d'échec. */
const SEUIL_PARTIES_TAUX = 4;

const formaterDate = (valeur) => (valeur
  ? new Date(valeur).toLocaleDateString('fr-FR', {
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
  })
  : '—');

/** Libellé d'un niveau de taxonomie filtré : absent du filtre, « sans », ou la valeur. */
const libelleNiveau = (valeur, tous, sans, libelle) => {
  if (valeur === undefined) return tous;
  if (valeur === '') return sans;
  return libelle(valeur) || valeur;
};

const ExerciseListScreen = () => {
  const [exercises, setExercises] = useState([]);
  const [pagination, setPagination] = useState(() => ({ page: lirePageListe(), totalPages: 1, limit: 20 }));
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(lireFiltresListe);
  const [sources, setSources] = useState([]);
  const [types, setTypes] = useState([]);
  const [sourcesLoading, setSourcesLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, exerciseId: null });
  const [bulkCorrectionOpen, setBulkCorrectionOpen] = useState(false);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const hasUrlFilters = ['gameType', 'domain', 'competence', 'notion'].some((key) => searchParams.has(key));
    if (!hasUrlFilters) return;

    setFilters((prev) => {
      const next = { ...prev };
      if (searchParams.has('gameType')) next.gameType = searchParams.get('gameType') || '';
      if (searchParams.has('domain')) next.domain = searchParams.get('domain') ?? '';
      else delete next.domain;
      if (searchParams.has('competence')) next.competence = searchParams.get('competence') ?? '';
      else delete next.competence;
      if (searchParams.has('notion')) next.notion = searchParams.get('notion') ?? '';
      else delete next.notion;
      return ecrireFiltresListe(next);
    });
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, [searchParams]);

  useEffect(() => {
    ecrirePageListe(pagination.page);
    loadExercises();
  }, [pagination.page, filters]);

  // Les sources ne bougent qu'à l'insertion d'un lot : un seul chargement au montage.
  useEffect(() => {
    let annule = false;
    (async () => {
      try {
        const data = await AdminService.getExerciseSources();
        if (!annule) {
          setSources(data?.sources || []);
          setTypes(data?.types || []);
        }
      } catch (error) {
        console.error('Erreur chargement des sources:', error);
        if (!annule) setSources([]);
      } finally {
        if (!annule) setSourcesLoading(false);
      }
    })();
    return () => { annule = true; };
  }, []);

  const loadExercises = async () => {
    setLoading(true);
    try {
      const { exercises, pagination: paginationData } = await AdminService.getExercises({
        ...parametresApiListe(filters),
        page: pagination.page,
        limit: pagination.limit,
      });
      setExercises(exercises);
      setPagination(prev => ({ ...prev, ...paginationData }));
    } catch (error) {
      console.error('Erreur lors du chargement des exercices:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Options des trois filtres de taxonomie, dérivées du référentiel hydraté.
   *
   * Les valeurs envoyées sont des ID (`grammaire`, `classes_mots`,
   * `determinant_ou_pronom`) — l'API filtre là-dessus depuis la bascule 084/085.
   * Les libellés ne servent qu'à l'affichage. Avant, ces trois filtres étaient
   * des champs texte : il fallait connaître l'ID par cœur pour s'en servir.
   */
  const taxonomyTree = getAllAvailableNotions() || {};

  const domainOptions = Object.keys(taxonomyTree).map((id) => ({
    id,
    label: DOMAIN_NAMES[id] || id,
  }));

  // Sans domaine choisi on propose TOUTES les compétences, préfixées de leur
  // domaine : le filtre reste utilisable sans imposer l'ordre de la cascade.
  const competenceOptions = Object.entries(taxonomyTree)
    .filter(([domainId]) => !filters.domain || domainId === filters.domain)
    .flatMap(([domainId, comps]) => Object.keys(comps).map((id) => ({
      id,
      label: filters.domain
        ? getCompetenceName(domainId, id)
        : `${DOMAIN_NAMES[domainId] || domainId} · ${getCompetenceName(domainId, id)}`,
    })));

  const notionOptions = Object.entries(taxonomyTree)
    .filter(([domainId]) => !filters.domain || domainId === filters.domain)
    .flatMap(([domainId, comps]) => Object.entries(comps)
      .filter(([competenceId]) => !filters.competence || competenceId === filters.competence)
      .flatMap(([competenceId, notions]) => notions.map((id) => ({
        id,
        label: getNotionLabel(id) || id,
        competenceLabel: `${DOMAIN_NAMES[domainId] || domainId} · ${getCompetenceName(domainId, competenceId)}`,
      }))));

  /**
   * Un filtre de taxonomie efface ceux qui en dépendent : garder une notion
   * après avoir changé de domaine produirait une intersection vide, donc une
   * liste inexplicablement déserte.
   */
  const handleTaxonomyFilterChange = (niveau, value) => {
    setFilters((prev) => {
      const next = { ...prev, [niveau]: value };
      if (niveau === 'domain') { next.competence = ''; next.notion = ''; }
      if (niveau === 'competence') { next.notion = ''; }
      for (const cle of ['domain', 'competence', 'notion']) {
        if (next[cle] === '') delete next[cle];
      }
      return ecrireFiltresListe(next);
    });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => {
      const newFilters = { ...prev, [field]: value };
      if (['domain', 'competence', 'notion'].includes(field) && value === '') {
        delete newFilters[field];
      }
      return ecrireFiltresListe(newFilters);
    });
    // Reset to page 1 when filters change
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (event, value) => {
    setPagination(prev => ({ ...prev, page: value }));
  };

  /** Un clic trie par la colonne ; un second inverse le sens. */
  const handleSort = (colonne) => {
    setFilters((prev) => ecrireFiltresListe({
      ...prev,
      sortBy: colonne,
      sortDir: prev.sortBy === colonne && prev.sortDir === 'DESC' ? 'ASC' : 'DESC',
    }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const resetFilters = () => {
    setFilters(ecrireFiltresListe({ ...FILTRES_PAR_DEFAUT }));
    setPagination((prev) => ({ ...prev, page: 1 }));
    navigate('/admin/exercises');
  };

  const enTeteTriable = (colonne) => (
    <TableCell sortDirection={filters.sortBy === colonne ? filters.sortDir.toLowerCase() : false}>
      <TableSortLabel
        active={filters.sortBy === colonne}
        direction={filters.sortBy === colonne ? filters.sortDir.toLowerCase() : 'desc'}
        onClick={() => handleSort(colonne)}
      >
        {COLONNES_TRIABLES[colonne]}
      </TableSortLabel>
    </TableCell>
  );

  const clearTaxonomyFilters = () => {
    setFilters((prev) => {
      const next = { ...prev };
      delete next.domain;
      delete next.competence;
      delete next.notion;
      return ecrireFiltresListe(next);
    });
    setPagination((prev) => ({ ...prev, page: 1 }));
    navigate('/admin/exercises');
  };

  const hasTaxonomyFilter = filters.domain !== undefined
    || filters.competence !== undefined
    || filters.notion !== undefined;

  const handleAddNew = () => {
    navigate(ROUTES.ADMIN.EXERCISES.STUDIO);
  };

  const handleEdit = (id) => {
    navigate(ROUTES.ADMIN.EXERCISES.EDIT(id));
  };

  const handleView = (id) => {
    navigate(ROUTES.ADMIN.EXERCISES.VIEW(id));
  };

  const handleToggleActive = async (id) => {
    try {
      await AdminService.toggleExerciseStatus(id);
      // Update the local state
      setExercises(exercises.map(ex => 
        ex.id === id ? { ...ex, isActive: !ex.isActive } : ex
      ));
    } catch (error) {
      console.error('Erreur lors de la modification du statut:', error);
    }
  };

  const handleDeleteClick = (exerciseId) => {
    setDeleteDialog({ open: true, exerciseId });
  };

  const confirmDelete = async () => {
    try {
      await AdminService.deleteExercise(deleteDialog.exerciseId);
      // Remove from local state
      setExercises(exercises.filter(ex => ex.id !== deleteDialog.exerciseId));
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
    } finally {
      setDeleteDialog({ open: false, exerciseId: null });
    }
  };

  const renderGameTypeLabel = (gameType) => {
    let label = gameType;
    let color = '#6B8DE3';
    
    switch (gameType) {
      case GAME_TYPES.DICTATION.id:
        label = 'Dictée';
        color = '#4CAF50';
        break;
      case GAME_TYPES.SPEED_GAME.id:
        label = 'Jeu de vitesse';
        color = '#2196F3';
        break;
      case GAME_TYPES.CORRECTION_GAME.id:
        label = 'Correction';
        color = '#FF9800';
        break;
      case GAME_TYPES.TEXT_HOLES.id:
        label = 'Texte à trous';
        color = '#9C27B0';
        break;
      case GAME_TYPES.WORD_LISTS.id:
        label = 'Liste de mots';
        color = '#9C27B0';
        break;
      case GAME_TYPES.ASSOCIATION.id:
        label = 'Association';
        color = '#607D8B';
        break;
      case GAME_TYPES.CATEGORISATION.id:
        label = 'Catégorisation';
        color = '#795548';
        break;
      case GAME_TYPES.SPEED_SORT.id:
        label = 'Speed Sort';
        color = '#E91E63';
        break;
    }
    
    return (
      <Chip 
        label={label}
        style={{ 
          background: color,
          color: 'white' 
        }}
      />
    );
  };

  if (loading && exercises.length === 0) {
    return (
      <AdminLayout>
        <LoadingScreen />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5">
            Liste des exercices ({pagination.totalItems || 0})
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<AutoFixHigh />}
              onClick={() => setBulkCorrectionOpen(true)}
            >
              Correction en masse
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Add />}
              onClick={handleAddNew}
            >
              Nouvel exercice
            </Button>
          </Box>
        </Box>

        {hasTaxonomyFilter && (
          <Alert
            severity="info"
            sx={{ mb: 2 }}
            action={(
              <Button color="inherit" size="small" onClick={clearTaxonomyFilters}>
                Effacer taxonomie
              </Button>
            )}
          >
            Filtre taxonomie :
            {' '}
            {/* Libellés, pas ID : c'est un bandeau destiné à l'œil humain.
                L'ID reste en infobulle pour qui doit le reprendre en SQL. */}
            <strong title={`${filters.domain ?? '—'} / ${filters.competence ?? '—'} / ${filters.notion ?? '—'}`}>
              {libelleNiveau(filters.domain, 'tous domaines', 'sans domaine', (d) => DOMAIN_NAMES[d])}
              {' / '}
              {libelleNiveau(filters.competence, 'toutes compétences', 'sans compétence',
                (c) => getCompetenceName(filters.domain, c))}
              {' / '}
              {libelleNiveau(filters.notion, 'toutes notions', 'sans notion', getNotionLabel)}
            </strong>
          </Alert>
        )}

        {/* Filters */}
        <Paper sx={{ mb: 3, p: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={2}>
              <TextField
                select
                fullWidth
                label="Type de jeu"
                value={filters.gameType}
                onChange={(e) => handleFilterChange('gameType', e.target.value)}
              >
                <MenuItem value="">Tous</MenuItem>
                {Object.values(GAME_TYPES).map((game) => (
                  <MenuItem key={game.id} value={game.id}>{game.label}</MenuItem>
                ))} 
              </TextField>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                select
                fullWidth
                label="Origine"
                value={filters.type}
                onChange={(e) => handleFilterChange('type', e.target.value)}
              >
                <MenuItem value="">Toutes</MenuItem>
                {types.map((t) => (
                  <MenuItem key={t.type} value={t.type}>
                    {TYPE_LABELS[t.type] || t.type} ({t.total})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                select
                fullWidth
                label="Statut"
                value={filters.active}
                onChange={(e) => handleFilterChange('active', e.target.value)}
              >
                <MenuItem value="">Tous</MenuItem>
                <MenuItem value="true">Actif</MenuItem>
                <MenuItem value="false">Inactif</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                select
                fullWidth
                label="Signalés"
                value={filters.flagged}
                onChange={(e) => handleFilterChange('flagged', e.target.value)}
              >
                <MenuItem value="">Tous</MenuItem>
                <MenuItem value="true">Signalés</MenuItem>
                <MenuItem value="false">Non signalés</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                select
                fullWidth
                label="Validation"
                value={filters.validationStatus}
                onChange={(e) => handleFilterChange('validationStatus', e.target.value)}
              >
                <MenuItem value="">Tous</MenuItem>
                <MenuItem value="nouveau">Nouveau</MenuItem>
                <MenuItem value="en_attente">En attente de validation</MenuItem>
                <MenuItem value="valide">Validé</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <Autocomplete
                options={sources}
                loading={sourcesLoading}
                value={sources.find((s) => s.source === filters.source) || null}
                onChange={(e, option) => handleFilterChange('source', option ? option.source : '')}
                getOptionLabel={(option) => SOURCE_LABELS[option.source] || option.source}
                isOptionEqualToValue={(option, value) => option.source === value.source}
                renderOption={(props, option) => (
                  <li {...props} key={option.source}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                      <Typography variant="body2">
                        {SOURCE_LABELS[option.source] || option.source}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.actifs} actif{option.actifs > 1 ? 's' : ''}
                        {' · '}{option.valides} validé{option.valides > 1 ? 's' : ''}
                        {option.total !== option.actifs ? ` · ${option.total - option.actifs} inactif${option.total - option.actifs > 1 ? 's' : ''}` : ''}
                        {option.dernier ? ` · ${new Date(option.dernier).toLocaleDateString('fr-FR')}` : ''}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Source (lot de génération)"
                    placeholder="Toutes — tapez pour chercher"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                select
                fullWidth
                label="Domaine"
                value={filters.domain ?? ''}
                onChange={(e) => handleTaxonomyFilterChange('domain', e.target.value)}
              >
                <MenuItem value="">Tous</MenuItem>
                {domainOptions.map((d) => (
                  <MenuItem key={d.id} value={d.id}>{d.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                select
                fullWidth
                label="Compétence"
                value={filters.competence ?? ''}
                onChange={(e) => handleTaxonomyFilterChange('competence', e.target.value)}
              >
                <MenuItem value="">Toutes</MenuItem>
                {competenceOptions.map((c) => (
                  <MenuItem key={c.id} value={c.id}>{c.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={2}>
              {/* Autocomplete et non Select : 103 notions, on les cherche au clavier.
                  L'option porte le LIBELLÉ, la valeur envoyée est l'ID — c'est ce
                  que la base filtre depuis 084/085, et les confondre viderait la
                  liste en silence à chaque renommage. */}
              <Autocomplete
                fullWidth
                options={notionOptions}
                value={notionOptions.find((n) => n.id === (filters.notion ?? '')) ?? null}
                onChange={(e, option) => handleTaxonomyFilterChange('notion', option?.id ?? '')}
                getOptionLabel={(option) => option.label || ''}
                isOptionEqualToValue={(option, valeur) => option.id === valeur.id}
                groupBy={(option) => option.competenceLabel}
                noOptionsText="Aucune notion"
                renderInput={(params) => (
                  <TextField {...params} label="Notion" placeholder="Toutes" />
                )}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Recherche"
                placeholder="Titre, description..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                select
                fullWidth
                label="Filtrer par date"
                value={filters.dateField || 'created_at'}
                onChange={(e) => handleFilterChange('dateField', e.target.value)}
              >
                <MenuItem value="created_at">Date de création</MenuItem>
                <MenuItem value="updated_at">Date de modification</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Du"
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Au"
                value={filters.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6} md={1}>
              <TextField
                fullWidth
                type="number"
                label="Joué ≥"
                title="Nombre de parties où l'exercice a été joué (au moins)"
                value={filters.partiesMin}
                onChange={(e) => handleFilterChange('partiesMin', e.target.value)}
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={6} md={1}>
              <TextField
                fullWidth
                type="number"
                label="Joué ≤"
                title="Nombre de parties (au plus) — 0 pour les exercices jamais joués"
                value={filters.partiesMax}
                onChange={(e) => handleFilterChange('partiesMax', e.target.value)}
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={6} md={1}>
              <TextField
                fullWidth
                type="number"
                label="Échec ≥ %"
                title={`Taux d'échec minimal — seuls les exercices joués au moins ${SEUIL_PARTIES_TAUX} fois en ont un`}
                value={filters.echecMin}
                onChange={(e) => handleFilterChange('echecMin', e.target.value)}
                inputProps={{ min: 0, max: 100 }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <IconButton color="primary" onClick={loadExercises} title="Recharger">
                <Refresh />
              </IconButton>
              <Button size="small" onClick={resetFilters}>
                Réinitialiser
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Exercises Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                {enTeteTriable('id')}
                <TableCell>Titre</TableCell>
                <TableCell>Notion</TableCell>
                {enTeteTriable('game_type')}
                <TableCell>Niveau</TableCell>
                <TableCell>Source</TableCell>
                {enTeteTriable('created_at')}
                {enTeteTriable('updated_at')}
                {enTeteTriable('parties')}
                {enTeteTriable('taux_echec')}
                <TableCell>Actif</TableCell>
                {enTeteTriable('validation_status')}
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {exercises.length > 0 ? (
                exercises.map((exercise) => (
                  <TableRow
                    key={exercise.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleEdit(exercise.id)}
                  >
                    <TableCell>{exercise.id}</TableCell>
                    <TableCell>{exercise.title}</TableCell>
                    {/* Notion servie par le référentiel (jointure côté API depuis la
                        bascule 084/085 : l'exercice ne porte plus que `notion_id`).
                        Un exercice sans notion est un trou réel, pas un défaut
                        d'affichage — on le signale au lieu de le laisser vide. */}
                    <TableCell>
                      {exercise.notionLabel ? (
                        <Typography variant="body2" title={exercise.notionId}>
                          {exercise.notionLabel}
                        </Typography>
                      ) : (
                        <Chip label="sans notion" size="small" color="warning" variant="outlined" />
                      )}
                      {exercise.competenceLabel && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {exercise.domainLabel} · {exercise.competenceLabel}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{renderGameTypeLabel(exercise.gameType)}</TableCell>
                    <TableCell>
                      {exercise.cycle
                        ? `Cycle ${exercise.cycle}`
                        : exercise.schoolLevel || exercise.level || '—'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={SOURCE_LABELS[exercise.source] || exercise.source || '—'}
                        title={exercise.type ? `Origine : ${TYPE_LABELS[exercise.type] || exercise.type}` : undefined}
                        color={exercise.type === 'llm' ? 'secondary' : 'primary'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {formaterDate(exercise.createdAt)}
                    </TableCell>
                    <TableCell>
                      {formaterDate(exercise.updatedAt)}
                    </TableCell>
                    <TableCell align="right">
                      {exercise.parties || '—'}
                    </TableCell>
                    <TableCell
                      align="right"
                      title={exercise.questions
                        ? `${exercise.echecs} échec(s) sur ${exercise.questions} question(s), ${exercise.parties} partie(s)`
                        : undefined}
                    >
                      {exercise.tauxEchec != null ? (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`${Math.round(exercise.tauxEchec * 100)} %`}
                          color={exercise.tauxEchec >= 0.5 ? 'error' : exercise.tauxEchec >= 0.3 ? 'warning' : 'default'}
                        />
                      ) : '—'}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Chip 
                        label={exercise.isActive ? 'Actif' : 'Inactif'} 
                        color={exercise.isActive ? 'success' : 'error'}
                        size="small"
                        onClick={() => handleToggleActive(exercise.id)}
                      />
                      {exercise.isFlagged && (
                        <Chip label="Signalé" color="warning" size="small" sx={{ ml: 0.5 }} />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={
                          exercise.validationStatus === 'valide'
                            ? 'Validé'
                            : exercise.validationStatus === 'en_attente'
                              ? 'En attente'
                              : 'Nouveau'
                        }
                        color={
                          exercise.validationStatus === 'valide'
                            ? 'success'
                            : exercise.validationStatus === 'en_attente'
                              ? 'warning'
                              : 'default'
                        }
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <IconButton onClick={() => handleEdit(exercise.id)} title="Modifier">
                        <Edit />
                      </IconButton>
                      <IconButton onClick={() => handleView(exercise.id)} title="Voir">
                        <Visibility />
                      </IconButton>
                      <IconButton onClick={() => handleDeleteClick(exercise.id)} title="Supprimer" color="error">
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={13} align="center">
                    Aucun exercice trouvé
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination 
            count={pagination.totalPages} 
            page={pagination.page} 
            onChange={handlePageChange} 
            color="primary" 
          />
        </Box>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, exerciseId: null })}>
          <DialogTitle>Confirmer la suppression</DialogTitle>
          <DialogContent>
            Êtes-vous sûr de vouloir supprimer cet exercice ? Cette action est irréversible.
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialog({ open: false, exerciseId: null })}>
              Annuler
            </Button>
            <Button onClick={confirmDelete} variant="contained" color="error">
              Supprimer
            </Button>
          </DialogActions>
        </Dialog>

        {/* Bulk Correction Modal */}
        <BulkCorrectionModal
          open={bulkCorrectionOpen}
          onClose={() => setBulkCorrectionOpen(false)}
          onExercisesUpdated={loadExercises}
          currentFilters={filters}
        />
      </Box>
    </AdminLayout>
  );
};

export default ExerciseListScreen; 